import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
// JD Simplified styles are already included in the main index.css
import { AuthProvider } from './lib/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FlagProvider } from '@unleash/proxy-client-react';
import { unleashConfig, unleashEnabled } from './lib/feature-flags';
import { UnleashContextSync } from './lib/UnleashContextSync';
import { UsermavenAnalyticsProvider } from './contexts/UsermavenContext';
import { initChunkErrorHandler } from './lib/chunk-error-handler';
import { initVersionCheck } from './lib/version-check';
import { initializeCapacitor, isNativeApp } from './lib/capacitor';

// Initialize chunk error handler to handle app updates gracefully
initChunkErrorHandler();

// Initialize proactive version check (polls /version.json for new deploys)
initVersionCheck();

// Theme is applied pre-paint by /theme-detect.js (reads localStorage 'ui-theme',
// the key ThemeProvider uses) and managed at runtime by ThemeProvider in App.tsx.
// The old initializeTheme() call was removed: it read the WRONG key ('theme'),
// ran post-paint (deferred module), and CLOBBERED the correct .dark class that
// theme-detect.js sets — causing a white/light flash on dark-mode hard refresh
// before ThemeProvider restored dark. See board task PY0RKKdR9O8I7dshsAig.

// Pre-rendered SEO content (#seo-content) is hidden by default via CSS
// and removed from the DOM in App.tsx once React mounts

// Initialize Capacitor for native platforms
// This sets up status bar, keyboard handling, and app lifecycle listeners
// Note: Deep links are initialized separately in App.tsx with router access
if (isNativeApp) {
  initializeCapacitor().catch(err => {
    console.error('[Capacitor] Initialization failed:', err);
  });
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes (renamed from cacheTime in v5)
      refetchOnWindowFocus: false,
      placeholderData: undefined, // replaces keepPreviousData
    },
  },
});

// Inner tree shared by both branches of the FlagProvider guard below.
// When unleashEnabled is false (env vars missing at build time), the
// FlagProvider is omitted and UnleashContextSync — which calls
// useUnleashContext() and throws outside a FlagProvider — is also omitted.
// All useFlag() consumers then fall back to the SDK's default-false behavior.
const appTree = (
  <QueryClientProvider client={queryClient}>
    <UsermavenAnalyticsProvider>
      <AuthProvider>
        {unleashEnabled && <UnleashContextSync />}
        <App />
      </AuthProvider>
    </UsermavenAnalyticsProvider>
    {/* Temporarily disabled for testing */}
    {/* {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
  </QueryClientProvider>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  // React StrictMode intentionally mounts components twice in development to help find issues
  // This is why you might see two AuthProvider mounts in the console - it's normal in dev mode
  // IMPORTANT: There should only be ONE AuthProvider in the entire app tree.
  // This double mounting is ONLY in development and doesn't indicate a bug.
  // DO NOT add additional AuthProviders in your route components!
  <React.StrictMode>
    {unleashEnabled ? (
      <FlagProvider config={unleashConfig}>{appTree}</FlagProvider>
    ) : (
      appTree
    )}
  </React.StrictMode>
);